import { useState, useEffect, forwardRef } from "react";
import axios from "axios";

const UserForm = forwardRef(
  (
    {
      isEditing,
      user,
      setIsEditing,
      setIsAdding,
      accounts,
      unions,
      setUsers,
      setStagedUsers,
      setDialog,
    },
    ref // Accept ref prop
  ) => {
    const [formData, setFormData] = useState({
      displayName: "",
      email: "",
      employeeNumber: "",
      payRate: 0,
      payType: [],
      role: "unassigned",
      union: "non-union",
      active: true,
      isTemporary: false,
      account: null,
      hoursPerWeek: null,
      collectsTax: false,
      taxRate: 0,
      wsib: true,
      phoneNumber: "",
    });

    useEffect(() => {
      if (isEditing && user) {
        setFormData({
          ...user,
          payType: user.payType || [],
          hoursPerWeek: user.hoursPerWeek || null,
          collectsTax: user.collectsTax || false,
          taxRate: user.taxRate || 0,
          wsib: user.wsib !== undefined ? user.wsib : true,
          phoneNumber: user.phoneNumber || "",
        });
      }
    }, [isEditing, user]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const url = isEditing
          ? `https://erp-production-72da01c8e651.herokuapp.com/api/users/${user._id}`
          : `https://erp-production-72da01c8e651.herokuapp.com/api/users`;
        const method = isEditing ? "put" : "post";

        const response = await axios[method](url, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (isEditing) {
          setUsers((prev) =>
            prev.map((u) => (u._id === user._id ? response.data.user : u))
          );
          setStagedUsers((prev) =>
            prev.map((u) => (u._id === user._id ? response.data.user : u))
          );
        } else {
          setUsers((prev) =>
            [...prev, response.data.user].sort((a, b) =>
              a.displayName.localeCompare(b.displayName)
            )
          );
          setStagedUsers((prev) =>
            [...prev, response.data.user].sort((a, b) =>
              a.displayName.localeCompare(b.displayName)
            )
          );
        }

        setDialog({
          open: true,
          type: "success",
          title: "Success",
          message: isEditing
            ? "User updated successfully!"
            : "User added successfully!",
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          showCopyButton: false,
        });

        setIsEditing(false);
        setIsAdding(false);
      } catch (err) {
        console.error(`Error ${isEditing ? "updating" : "adding"} user:`, err);
        setDialog({
          open: true,
          type: "error",
          title: "Error",
          message: `Failed to ${
            isEditing ? "update" : "add"
          } user. Please try again.`,
          onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
          showCopyButton: false,
        });
      }
    };

    const handleCancel = () => {
      setIsEditing(false);
      setIsAdding(false);
      setFormData({
        displayName: "",
        email: "",
        employeeNumber: "",
        payRate: 0,
        payType: [],
        role: "unassigned",
        union: "non-union",
        active: true,
        isTemporary: false,
        account: null,
        hoursPerWeek: null,
        collectsTax: false,
        taxRate: 0,
        wsib: true,
        phoneNumber: "",
      });
    };

    return (
      <div
        ref={ref} // Forward the ref to the root div
        className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50"
      >
        <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full">
          <h3 className="text-lg font-medium text-white mb-4">
            {isEditing ? "Edit User" : "Add New User"}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Display Name
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Employee Number
              </label>
              <input
                type="text"
                name="employeeNumber"
                value={formData.employeeNumber}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Pay Rate
              </label>
              <input
                type="number"
                name="payRate"
                value={formData.payRate}
                onChange={handleChange}
                step="0.01"
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="unassigned">Unassigned</option>
                <option value="admin">Admin</option>
                <option value="foreman">Foreman</option>
                <option value="project_manager">Project Manager</option>
                <option value="site_worker">Site Worker</option>
                <option value="estimator">Estimator</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Union
              </label>
              <select
                name="union"
                value={formData.union}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="non-union">Non-Union</option>
                {unions.map((union) => (
                  <option key={union._id} value={union.title}>
                    {union.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded"
              />
              <label className="text-sm font-medium text-gray-400">
                Active
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="isTemporary"
                checked={formData.isTemporary}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded"
              />
              <label className="text-sm font-medium text-gray-400">
                Temporary User
              </label>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-600 text-gray-200 px-4 py-2 rounded-md shadow-md hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md shadow-md hover:bg-indigo-500 transition-colors"
              >
                {isEditing ? "Update User" : "Add User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
);

export default UserForm;
